export type ReviewPresentation =
  | { status: "pending" }
  | {
      status: "complete"
      verdict: "ready" | "revise" | "qualified" | "blocked"
      summary: string
      findings: { location: string; message: string; suggestedFix: string }[]
      limitations: string[]
    }

function record(value: unknown): Record<string, unknown> | undefined {
  if (value && typeof value === "object" && !Array.isArray(value)) return value as Record<string, unknown>
}

function prose(value: unknown) {
  return typeof value === "string" ? value.trim() : ""
}

export function reviewPresentation(text: string, completed: boolean): ReviewPresentation | undefined {
  const source = text.trim().replace(/^```(?:json)?\s*\n?/i, "").replace(/\s*```$/, "").trim()
  if (!source.startsWith("{") && !/^```(?:json)?\s*$/i.test(text.trim())) return
  // Keep the machine payload out of chat while its text is still arriving.
  if (!completed) return { status: "pending" }

  let review: Record<string, unknown> | undefined
  try {
    review = record(JSON.parse(source))
  } catch {
    return
  }
  if (!review || !["1", "review.v1"].includes(String(review.contract_version ?? review.schema_version))) return
  const verdict = review.verdict
  if (verdict !== "ready" && verdict !== "revise" && verdict !== "qualified" && verdict !== "blocked") return
  const summary = prose(review.summary)
  if (!summary || !Array.isArray(review.findings)) return

  return {
    status: "complete",
    verdict,
    summary,
    findings: review.findings.flatMap((value) => {
      const finding = record(value)
      if (!finding) return []
      const message = prose(finding.message ?? finding.explanation)
      if (!message) return []
      return [
        {
          location: prose(finding.location ?? finding.affected_location),
          message,
          suggestedFix: prose(finding.suggested_fix ?? finding.actionable_repair),
        },
      ]
    }),
    limitations: Array.isArray(review.not_checked) ? review.not_checked.map(prose).filter(Boolean) : [],
  }
}
